import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const T = {
  en: { head: "Certificate of Reading", given: "This certificate is proudly given to", for: "for mastering", level: "Level", lessons: "Lessons mastered",
    on: "Date", sign: "Kiko, your reading friend", print: "Print", pdf: "Download PDF", back: "Back", none: "This certificate is not unlocked yet." },
  fr: { head: "Certificat de Lecture", given: "Ce certificat est fièrement remis à", for: "pour avoir réussi", level: "Niveau", lessons: "Leçons réussies",
    on: "Date", sign: "Kiko, ton ami de lecture", print: "Imprimer", pdf: "Télécharger le PDF", back: "Retour", none: "Ce certificat n'est pas encore débloqué." },
};

const fmt = (d, lang) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" }) : "");

function Sheet({ c, name, t, lang, sheetRef }) {
  return (
    <div ref={sheetRef} className="relative w-full aspect-[1.414] bg-[#FFFBEB] rounded-xl overflow-hidden text-slate-900 font-kids" data-testid="certificate-sheet">
      <div className="absolute inset-3 sm:inset-4 border-[6px] border-double border-[#F59E0B] rounded-lg" />
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(#FACC15 1.5px, transparent 1.5px)", backgroundSize: "22px 22px" }} />
      <div className="relative h-full flex flex-col items-center justify-center text-center px-[8%] py-[5%] gap-[1.6%]">
        <p className="text-[2.2vw] sm:text-base font-bold uppercase tracking-[0.3em] text-[#B45309]">Anou Appran</p>
        <h1 className="text-[5vw] sm:text-4xl lg:text-5xl font-bold text-[#1E3A8A]" data-testid="certificate-title">{t.head}</h1>
        <p className="text-[2.4vw] sm:text-lg">{t.given}</p>
        <p className="text-[6vw] sm:text-5xl lg:text-6xl font-bold text-[#DC2626]" data-testid="certificate-child">{name}</p>
        <p className="text-[2.4vw] sm:text-lg">{t.for} <b>{t.level} {c.level} : {c.name}</b></p>
        <p className="text-[1.8vw] sm:text-sm text-slate-600 max-w-[80%]" data-testid="certificate-lessons">{t.lessons} : {c.lessons.join(" · ")}</p>
        <div className="w-full flex items-end justify-between mt-[2%]">
          <div className="text-left">
            <p className="text-[1.8vw] sm:text-sm text-slate-500">{t.on}</p>
            <p className="text-[2.4vw] sm:text-lg font-bold" data-testid="certificate-date">{fmt(c.date, lang)}</p>
          </div>
          <img src="/mascot/kiko.png" alt="Kiko" crossOrigin="anonymous" className="w-[16%] drop-shadow-lg" />
          <div className="text-right">
            <p className="text-[2.6vw] sm:text-xl font-bold italic text-[#15803D]">Kiko</p>
            <p className="text-[1.8vw] sm:text-sm text-slate-500">{t.sign}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Printable Reading certificate (one per mastered level), in the language of that level.
export default function ReadingCertificate() {
  const { childId, lang, level } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const t = T[lang] || T.en;
  useEffect(() => {
    api.get(`/reading/${childId}/certificates`).then(({ data: d }) => setData(d)).catch(() => setData(false));
  }, [childId]);
  const c = data && data.certificates.find((x) => x.lang === lang && String(x.level) === String(level));
  const pdf = async () => {
    setBusy(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([import("html2canvas"), import("jspdf")]);
      const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: "#FFFBEB", useCORS: true });
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      doc.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, 297, 210);
      doc.save(`${lang === "fr" ? "certificat-lecture" : "reading-certificate"}-${data.child_name}-${lang}-${level}.pdf`);
    } catch {
      toast.error(lang === "fr" ? "Le PDF n'a pas pu être créé." : "The PDF could not be created.");
    }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-slate-100 print:bg-white">
      <header className="max-w-5xl mx-auto px-5 pt-5 flex flex-wrap items-center gap-3 print:hidden">
        <button onClick={() => navigate(-1)} className="rounded-full bg-white border px-4 h-10 font-bold inline-flex items-center gap-2" data-testid="certificate-back"><ArrowLeft size={16} /> {t.back}</button>
        <div className="flex-1" />
        {c && (
          <>
            <button onClick={() => window.print()} className="rounded-full bg-white border px-4 h-10 font-bold inline-flex items-center gap-2" data-testid="certificate-print"><Printer size={16} /> {t.print}</button>
            <button onClick={pdf} disabled={busy} className="rounded-full bg-blue-600 text-white px-4 h-10 font-bold inline-flex items-center gap-2 disabled:opacity-60" data-testid="certificate-pdf"><Download size={16} /> {t.pdf}</button>
          </>
        )}
      </header>
      <main className="max-w-5xl mx-auto p-5 print:p-0 print:max-w-none">
        {data === null && <p className="text-center p-10">…</p>}
        {data !== null && !c && <p className="text-center p-10 font-bold" data-testid="certificate-locked">{t.none}</p>}
        {c && <div className="shadow-2xl rounded-xl print:shadow-none"><Sheet c={c} name={data.child_name} t={t} lang={lang} sheetRef={ref} /></div>}
      </main>
      <style>{"@media print { @page { size: A4 landscape; margin: 0; } }"}</style>
    </div>
  );
}
